/**
 * SkillDetailPage - Skill detay sayfasi
 * Layout: Hero + tam genislik tab sistemi (Overview / Community / Versions)
 */
import { ArrowLeft, Clock, Copy, MessageSquare, Play, Terminal, Zap } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import SkillCommunity from './components/SkillCommunity'
import SkillDetailHero from './components/SkillDetailHero'
import SkillOverview from './components/SkillOverview'
import SkillRunModal from './components/SkillRunModal'
import SkillVersions from './components/SkillVersions'
import { useSkillDetail, useSkillPosts, useSkillVersions } from './hooks/useSkills'

type Tab = 'overview' | 'run' | 'cli' | 'versions'

export default function SkillDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { skill, loading, error, install, toggle } = useSkillDetail(id)
  const [tab, setTab] = useState<Tab>('overview')
  const [installing, setInstalling] = useState(false)
  const [showRunModal, setShowRunModal] = useState(false)
  const [newPost, setNewPost] = useState('')
  const [posting, setPosting] = useState(false)
  const [copied, setCopied] = useState(false)

  const { posts, loading: postsLoading, createPost, reload } = useSkillPosts(id, true)
  const { versions, currentChangelog } = useSkillVersions(id)

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
        Loading skill...
      </div>
    )
  }

  if (error || !skill) {
    return (
      <div style={{ padding: '60px', textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error ?? 'Skill not found'}</p>
        <button
          onClick={() => navigate(-1)}
          style={{ padding: '8px 16px', borderRadius: '8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', cursor: 'pointer' }}
        >
          Go Back
        </button>
      </div>
    )
  }

  const accent = skill.color ?? '#3b82f6'

  const handleAction = async () => {
    setInstalling(true)
    if (skill.is_installed) {
      await toggle()
    } else {
      await install()
    }
    setInstalling(false)
  }

  const handlePost = async () => {
    if (!newPost.trim() || !user) {return}
    setPosting(true)
    const ok = await createPost(newPost.trim(), user.id)
    if (ok) {setNewPost('')}
    setPosting(false)
  }

  const skillSlug = (skill.name ?? '').toLowerCase().replace(/\s+/g, '-')
  const cliCommands = [
    `aicloud skill install ${skillSlug}`,
    `aicloud skill run ${skillSlug} --query "example"`,
    `aicloud skill info ${skillSlug}`,
    `aicloud skill logs ${skillSlug} --last 10`,
  ]

  const copyCmd = (cmd: string) => {
    void navigator.clipboard.writeText(cmd)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <Zap size={13} /> },
    { key: 'run', label: 'Run', icon: <Play size={13} /> },
    { key: 'cli', label: 'CLI', icon: <Terminal size={13} /> },
    { key: 'versions', label: 'Versions', icon: <Clock size={13} /> },
  ]

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('/aicloud/app/skills')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: '13px', padding: '0 0 16px 0',
        }}
      >
        <ArrowLeft size={13} /> Back to Marketplace
      </button>

      {/* Hero */}
      <SkillDetailHero skill={skill} installing={installing} onAction={() => { void handleAction() }} />

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: '4px', marginBottom: '20px',
        borderBottom: '1px solid var(--border-color)', paddingBottom: '0',
      }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', fontSize: '13px', fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t.key ? accent : 'var(--text-secondary)',
              borderBottom: tab === t.key ? `2px solid ${accent}` : '2px solid transparent',
              marginBottom: '-1px', transition: 'all .15s',
            }}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <>
          <SkillOverview skill={skill} currentChangelog={currentChangelog} onInstall={() => { void handleAction() }} />
          {/* Community - inline, Overview'in altinda */}
          <div style={{ marginTop: '28px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              marginBottom: '16px', paddingBottom: '12px',
              borderBottom: '1px solid var(--border-color)',
            }}>
              <MessageSquare size={16} color={accent} />
              <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>Community</span>
              {posts.length > 0 && (
                <span style={{
                  fontSize: '11px', fontWeight: 700, padding: '1px 8px',
                  borderRadius: '10px', background: `${accent}20`, color: accent,
                }}>{posts.length}</span>
              )}
            </div>
            <SkillCommunity
              posts={posts}
              loading={postsLoading}
              accent={accent}
              isLoggedIn={!!user}
              newPost={newPost}
              posting={posting}
              skillId={id}
              onNewPostChange={setNewPost}
              onPost={() => { void handlePost() }}
              onReload={() => { void reload() }}
              fullWidth
            />
          </div>
        </>
      )}

      {/* Run Tab */}
      {tab === 'run' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            padding: '20px', borderRadius: 14,
            background: `linear-gradient(135deg, ${accent}08, ${accent}04)`,
            border: `1px solid ${accent}25`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Play size={15} color={accent} />
              <span style={{ fontSize: 14, fontWeight: 700 }}>Run {skill.name}</span>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
              {skill.is_installed
                ? 'This skill is installed and ready to run. Click the button below to execute it with custom parameters.'
                : 'Install this skill first to run it.'}
            </p>
            <button
              onClick={() => skill.is_installed && setShowRunModal(true)}
              disabled={!skill.is_installed}
              style={{
                padding: '10px 20px', borderRadius: 10,
                background: skill.is_installed ? accent : 'var(--border-color)',
                color: skill.is_installed ? '#fff' : 'var(--text-secondary)',
                border: 'none', fontSize: 13, fontWeight: 600,
                cursor: skill.is_installed ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Play size={14} />
              {skill.is_installed ? 'Run Skill' : 'Install First'}
            </button>
          </div>
          {skill.usage_example && (
            <div style={{
              padding: '14px 16px', borderRadius: 12,
              background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Example Parameters
              </div>
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'var(--body-bg)', border: '1px solid var(--border-color)',
                fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)',
              }}>
                {skill.usage_example}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CLI Tab */}
      {tab === 'cli' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{
            padding: '16px', borderRadius: 14,
            background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Terminal size={14} color="#10b981" />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#e6edf3' }}>CLI Commands</span>
              {copied && (
                <span style={{ fontSize: 10, color: '#10b981', marginLeft: 'auto' }}>Copied!</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cliCommands.map(cmd => (
                <div key={cmd} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <span style={{ color: '#10b981', fontSize: 12 }}>$</span>
                  <code style={{ flex: 1, fontSize: 12, color: '#e6edf3', fontFamily: 'monospace' }}>
                    {cmd}
                  </code>
                  <button
                    onClick={() => copyCmd(cmd)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(255,255,255,0.4)', padding: '2px',
                    }}
                  >
                    <Copy size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div style={{
            padding: '14px 16px', borderRadius: 12,
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6,
          }}>
            Install the CLI: <code style={{ color: accent }}>pip install aicloud-cli</code>
            {' '}then run <code style={{ color: accent }}>aicloud init</code> to authenticate.
          </div>
        </div>
      )}

      {tab === 'versions' && (
        <SkillVersions
          currentVersion={skill.version}
          currentChangelog={currentChangelog}
          versions={versions}
          accent={accent}
          fullWidth
        />
      )}

      {/* Run Modal */}
      {showRunModal && <SkillRunModal skill={skill} onClose={() => setShowRunModal(false)} />}
    </div>
  )
}
